import { GoogleIcon } from '@app/assets';
import { IS_LOCALHOST, typedEnv } from '@app/environment/typed-env';
import { PARAM_GOOGLE_CLICK_ID } from '@app/gtag/google.constant';
import { EGTAGRegistrationOrigin, EGTAGRegistrationPopUp } from '@app/gtag/gtag.type';
import { useGoogleClickId } from '@app/gtag/useGoogleClickId';
import { PARAM_MICROSOFT_CLICK_ID } from '@app/gtag/microsoft.constant';
import { useMicrosoftClickId } from '@app/gtag/useMicrosoftClickId';
import { useCurrentLanguage } from '@app/i18n/useCurrentLanguage';
import {
  AUTH_QUERY_PARAM_AUTO_DOWNLOAD_QR_ID,
  AUTH_QUERY_PARAM_AUTO_LINK_QR_ID,
  AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_POPUP,
  AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_SOURCE,
  AUTH_QUERY_PARAM_GOOGLE_MODE,
  AUTH_QUERY_PARAM_LANGUAGE,
  AUTH_QUERY_PARAM_REDIRECT,
  AUTH_QUERY_PARAM_SUCCESS_REDIRECT,
  AUTH_QUERY_VALUE_GOOGLE_MODE_SIGN_IN,
} from '@app/modules/auth/auth.constants';
import { isSafeRedirectPath } from '@app/router/utils/redirectPath.utils';
import { SignUpInDto } from '@app/swagger-types';
import { checkTypeCompliance } from '@app/utils/type.utils';
import { useTranslation } from 'react-i18next';
import { StringParam, useQueryParam } from 'use-query-params';
import { Button } from '../button/Button';
import { Routes } from '@app/constants/routes';

checkTypeCompliance<keyof SignUpInDto, typeof AUTH_QUERY_PARAM_LANGUAGE>();
checkTypeCompliance<keyof SignUpInDto, typeof AUTH_QUERY_PARAM_AUTO_LINK_QR_ID>();

export const GoogleButton: React.FC<{
  autoLinkQRID?: string | null;
  onPreClick?: () => void;
  modeSignIn?: boolean;
  /**
   * widget popup flow: button reads "Download with Google" instead of "Start with Google"
   */
  modeDownload?: boolean;
  /**
   * null for sign-in case
   */
  origin: EGTAGRegistrationOrigin | null;
  /**
   * provide popup when origin = "generator"
   */
  popup?: EGTAGRegistrationPopUp | null;
}> = ({ autoLinkQRID, onPreClick, modeSignIn, modeDownload, origin, popup }) => {
  const { t } = useTranslation();
  const language = useCurrentLanguage();
  const { googleClickId } = useGoogleClickId();
  const { microsoftClickId } = useMicrosoftClickId();
  // Read-only - never call this setter (see docs/oauth2-dev-silent-bounce.md on why a setter
  // call here would be dangerous). Only meaningful for modeSignIn: that's the one caller
  // (SignInPage) a visitor can land on carrying a redirect target the route guard set.
  const [redirectTarget] = useQueryParam(AUTH_QUERY_PARAM_REDIRECT, StringParam);

  return (
    <Button
      onClick={() => {
        onPreClick?.();
        // TODO use env for api host
        const url = new URL(typedEnv.REACT_APP_OAUTH_GOOGLE_URL);
        if (IS_LOCALHOST) {
          url.searchParams.set(
            AUTH_QUERY_PARAM_SUCCESS_REDIRECT,
            encodeURIComponent(new URL(Routes.auth.oauth2_authorization, window.location.origin).toString())
          );
        }
        if (modeSignIn && isSafeRedirectPath(redirectTarget)) {
          // Rides along on the backend's existing generic session-attribute-forwarding
          // mechanism (every query param on this initial request comes back on the final
          // redirect) - no success-redirect/absolute-URL handling needed for this. Re-validated
          // again in OAuth2AuthorizationPage before it's ever used to navigate.
          url.searchParams.set(AUTH_QUERY_PARAM_REDIRECT, redirectTarget);
        }
        if (language) {
          url.searchParams.set(AUTH_QUERY_PARAM_LANGUAGE, language);
        }
        if (autoLinkQRID) {
          url.searchParams.set(AUTH_QUERY_PARAM_AUTO_LINK_QR_ID, autoLinkQRID);
          // TODO set success-redirect param when backend API ready to redirect to route like
          // /c/my-qr-codes?autoDownloadQRID=1NYX6311
          url.searchParams.set(AUTH_QUERY_PARAM_AUTO_DOWNLOAD_QR_ID, autoLinkQRID);
        }
        if (googleClickId) {
          url.searchParams.set(PARAM_GOOGLE_CLICK_ID, googleClickId);
        }
        if (microsoftClickId) {
          url.searchParams.set(PARAM_MICROSOFT_CLICK_ID, microsoftClickId);
        }
        if (modeSignIn) {
          url.searchParams.set(AUTH_QUERY_PARAM_GOOGLE_MODE, AUTH_QUERY_VALUE_GOOGLE_MODE_SIGN_IN);
        }
        if (origin) {
          url.searchParams.set(AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_SOURCE, origin);
        }
        if (popup) {
          url.searchParams.set(AUTH_QUERY_PARAM_FORWARDED_SIGN_UP_POPUP, popup);
        }
        const strURL = url.toString();
        // console.log('oauth2', strURL);
        window.location.href = strURL;
      }}
      variant="outlined"
      className="w-full"
    >
      <GoogleIcon className="mr-2" />
      {modeSignIn
        ? t('auth.continueWithGoogle')
        : modeDownload
        ? t('widget.signUpDialog.buttonDownloadWithGoogle')
        : t('auth.signUpStep.startWithGoogle')}
    </Button>
  );
};
